import { bookingService } from '@/core/api/services/bookingService';
import { logger } from '@/core/logger';
import { useAppStore, type ReservedUnit } from '@/core/stores/appStore';
import { applyAdhaBookingApiResult } from '@/modules/properties-web/utils/syncAdhaBookingFromApi';
import {
  mapProfileDetailsToUserPatch,
  profileService,
} from '@/modules/auth/services/profileService';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from './useTranslation';

export interface ReservationActionResult {
  readonly success: boolean;
  readonly message: string;
}

/**
 * Mirrors reservedUnit (mobile-only field) to whatever pendingUnitReservation
 * (shared with web) currently holds — call after any booking mutation so
 * Home's card / PropertyDetails stay in sync regardless of which screen
 * triggered the change.
 */
function syncReservedUnitFromPending(t: ReturnType<typeof useTranslation>['t']): void {
  const pending = useAppStore.getState().pendingUnitReservation;
  const current = useAppStore.getState().reservedUnit;
  if (!pending) {
    if (current) {
      useAppStore.getState().clearReservedUnit();
    }
    return;
  }
  useAppStore.getState().setReservedUnit({
    propertyId: pending.unitId,
    unitLabel: pending.unitTitle,
    image: current?.propertyId === pending.unitId ? (current.image ?? null) : null,
    badgeLabel: t('reserveUnit.pendingBadge'),
    ...(pending.parentId ? { parentId: pending.parentId } : {}),
    ...(pending.reservationStatus ? { reservationStatus: pending.reservationStatus } : {}),
    ...(pending.requestedOn ? { requestedOn: pending.requestedOn } : {}),
  });
}

/**
 * Shared "Resend Email" / "Cancel Reservation" logic — same code for
 * Home's reserved-unit card and PropertyDetailsScreenAdhaMobile's
 * Reservation Summary sheet, so a fix only needs to happen once.
 * Each screen keeps its own toast/dialog state; this hook just returns
 * the outcome (success + message) for the screen to display.
 */
export function useReservationActions(
  storeReservedUnit: Pick<ReservedUnit, 'propertyId' | 'parentId'> | null | undefined,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // After reserve/cancel: refresh booking + profile-details so `current_status`
  // overlays onto pending reservation (status messages stay in sync).
  const refreshBookingDetailAfterMutation = useCallback(async () => {
    const customerId = useAppStore.getState().auth.user?.customer_id?.trim();
    if (!customerId) {
      return;
    }
    try {
      const [response, profileDetails] = await Promise.all([
        bookingService.getBookingDetails(customerId),
        profileService.getProfileDetails(),
      ]);
      useAppStore.getState().setProfile(mapProfileDetailsToUserPatch(profileDetails));
      applyAdhaBookingApiResult({
        response,
        availableUnits: useAppStore.getState().availableUnits,
        resolveUnitTitle: (unit) => `${unit.unit_number || ''}`,
        profileDetails,
        convertedLead: useAppStore.getState().convertedLead,
        setPendingUnitReservation: useAppStore.getState().setPendingUnitReservation,
        clearAdhaWebBookingState: useAppStore.getState().clearAdhaWebBookingState,
        getPreviousPending: () => useAppStore.getState().pendingUnitReservation,
        upsertAvailableUnitFirst: useAppStore.getState().upsertAvailableUnitFirst,
      });
      await Promise.all([
        queryClient.resetQueries({ queryKey: ['adhaBooking', customerId] }),
        queryClient.resetQueries({ queryKey: ['adhaProfileDetails', customerId] }),
      ]);
      syncReservedUnitFromPending(t);
    } catch (error) {
      logger.warn('useReservationActions:refreshBookingDetailAfterMutation failed', error);
    }
  }, [queryClient, t]);

  const resendReservationEmail = useCallback(async (): Promise<ReservationActionResult> => {
    const parentId =
      storeReservedUnit?.parentId?.trim() || storeReservedUnit?.propertyId?.trim() || '';
    if (!parentId) {
      logger.warn('useReservationActions:resendEmail aborted — no parent_id');
      return { success: false, message: t('errors.unknownError') };
    }

    try {
      const response = await bookingService.resendBookingForm(parentId);
      logger.info('useReservationActions:resendBookingForm success', { response });
      return {
        success: true,
        message: response.message?.trim() || t('reserveUnit.emailResentSuccess'),
      };
    } catch (error) {
      logger.warn('useReservationActions:resendBookingForm failed', error);
      return { success: false, message: t('reserveUnit.resendEmailError') };
    }
  }, [storeReservedUnit?.parentId, storeReservedUnit?.propertyId, t]);

  /**
   * Cancel reuses POST /v1/bookings with an empty unit_id.
   * Customer comes from the bearer token.
   */
  const cancelReservation = useCallback(async (): Promise<ReservationActionResult> => {
    const unitId = storeReservedUnit?.propertyId?.trim() || '';
    if (!unitId) {
      logger.warn('useReservationActions:cancelReservation aborted — no propertyId');
      return { success: false, message: t('errors.unknownError') };
    }

    try {
      const response = await bookingService.cancelBooking();
      logger.info('useReservationActions:cancelBooking success', { response });
      useAppStore.getState().clearReservedUnit();
      useAppStore.getState().clearPendingUnitReservation();
      // Confirm booking + profile current_status in the background.
      void refreshBookingDetailAfterMutation();
      return {
        success: true,
        message: response.message?.trim() || t('reserveUnit.reservationCanceledSuccess'),
      };
    } catch (error) {
      logger.warn('useReservationActions:cancelBooking failed', error);
      return { success: false, message: t('reserveUnit.cancelReservationError') };
    }
  }, [storeReservedUnit?.propertyId, t, refreshBookingDetailAfterMutation]);

  return { resendReservationEmail, cancelReservation, refreshBookingDetailAfterMutation };
}
